/**
 * Sarvam AI Speech-to-Text adapter.
 *
 * Implements the SpeechProvider interface (see types.js) so QuestionSection
 * can use it interchangeably with the Azure adapter.
 *
 * Pipeline:
 *   Mic → PCM resampler (16kHz Int16, 250ms chunks)
 *       → JSON-wrapped + base64
 *       → WebSocket to /api/stt/sarvam-ws (Flask proxy)
 *       → wss://api.sarvam.ai/speech-to-text/ws (key added server-side)
 *
 *   Sarvam → proxy → WS message
 *          → handleSarvamMessage()
 *          → setRecognisingText (interim) OR commitPending() (on END_SPEECH)
 *
 * Interim vs final
 * ----------------
 * Sarvam's streaming protocol doesn't have an explicit "is_final" flag on
 * each transcript message. Strategy:
 *   - Every {type:"data"} updates `pendingPartial` (the latest interim text)
 *   - END_SPEECH VAD events commit the partial as a final utterance:
 *       transcriptionRef ← transcriptionRef + " " + pendingPartial
 *       addUserMessage(pendingPartial)
 *   - stopRecognition() sends a {type:"flush"} which usually triggers an
 *     END_SPEECH, then commits any leftover after a short grace period.
 *
 * React Strict Mode protection
 * ----------------------------
 * Module-level globals (`_globalWs`, `_globalResampler`, etc.) survive
 * component remounts so we don't open duplicate mics / WebSockets. The
 * `reuseExisting()` method reattaches refs to the live globals.
 */

import { createPcmResampler } from "./pcmResampler";

// ── Module-level globals (Strict Mode survival) ─────────────────────────────
let _globalWs = null;
let _globalResampler = null;
let _globalMediaStream = null;
let _globalInitialized = false;
// Lock: while createNewRecognizer is running, hold the in-flight Promise here
// so concurrent callers await it instead of starting a second creation that
// would tear down the first one mid-flight.
let _creatingPromise = null;

// How long to wait for WS handshake before bailing.
const WS_CONNECT_TIMEOUT_MS = 8000;

// After stopRecognition() sends flush, give Sarvam this long to send any
// final transcript via END_SPEECH before we force-commit whatever's pending.
const POST_STOP_COMMIT_GRACE_MS = 700;

// Auto-retry policy for transient WS disconnects mid-interview. Matches the
// Azure adapter's MAX_RETRY_ATTEMPTS so behavior is consistent across providers.
const RECONNECT_MAX_ATTEMPTS = 3;
const RECONNECT_BACKOFF_BASE_MS = 1000;

/**
 * Resolve the WebSocket URL for the Sarvam proxy. Defaults to same-origin
 * (works in production where Flask serves the React build, and in dev when
 * setupProxy.js forwards WS upgrades to the backend).
 *
 * Override at runtime by setting `window.__SARVAM_WS_URL_OVERRIDE` — useful
 * for local dev where the React dev server is on :3000 but Flask is on :5000
 * without a WS proxy:
 *
 *   window.__SARVAM_WS_URL_OVERRIDE = 'ws://127.0.0.1:5000/api/stt/sarvam-ws';
 */
function resolveSarvamWsUrl() {
    if (typeof window !== "undefined" && window.__SARVAM_WS_URL_OVERRIDE) {
        return window.__SARVAM_WS_URL_OVERRIDE;
    }
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${window.location.host}/api/stt/sarvam-ws`;
}

/**
 * @param {import('./types').SpeechProviderDeps} deps
 * @returns {import('./types').SpeechProvider}
 */
export function createSarvamProvider(deps) {
    const {
        recognizerRef,
        audioStreamRef,
        recognitionActiveRef,
        recognitionRetryCount,
        interviewActiveRef,
        transcriptionRef,
        setIsRecognising,
        setRecognisingText,
        setTranscription,
        setErrorMessage,
        setConvTick,
        onUserMessage,
    } = deps;

    // Latest uncommitted interim transcript. Updated on each {type:"data"}.
    // Committed on END_SPEECH event or after flush+grace.
    let pendingPartial = "";

    // ── Sarvam message handling ─────────────────────────────────────────────

    function commitPending() {
        if (!pendingPartial) return;
        const text = pendingPartial;
        // Clear FIRST to avoid double-commit if END_SPEECH and flush land close together.
        pendingPartial = "";

        transcriptionRef.current = transcriptionRef.current
            ? `${transcriptionRef.current} ${text}`
            : text;
        setTranscription(transcriptionRef.current);
        if (typeof onUserMessage === "function") {
            try {
                onUserMessage(text);
            } catch (e) {
                console.warn("[sarvam] onUserMessage callback failed:", e);
            }
        }
        setRecognisingText("");
        setConvTick((t) => t + 1);
    }

    function handleSarvamMessage(raw) {
        let msg;
        try {
            msg = JSON.parse(raw);
        } catch (e) {
            console.warn("[sarvam] non-JSON message from proxy:", String(raw).slice(0, 200));
            return;
        }

        const { type, data } = msg || {};

        if (type === "data" && data) {
            // IMPORTANT: Sarvam saaras:v3 sends one `data` message per
            // completed utterance, AFTER it has already fired END_SPEECH for
            // that utterance. So `data` arriving = "Sarvam finished
            // transcribing the last chunk of speech and here's the result".
            //
            // Treat each `data` as a final committed utterance:
            //   - append to transcriptionRef
            //   - call onUserMessage (becomes a permanent chat bubble)
            //   - briefly flash in setRecognisingText so the candidate sees
            //     it appear (even though Sarvam doesn't stream true partials,
            //     this gives a visible "your last sentence was understood" cue)
            const text = String(data.transcript || "").trim();
            if (text) {
                setRecognisingText(text);
                // Tiny delay before commit so the UI has a frame to render
                // the recognizing text before it becomes a permanent message.
                // Without this it can look jarring (text flashes in & out).
                transcriptionRef.current = transcriptionRef.current
                    ? `${transcriptionRef.current} ${text}`
                    : text;
                setTranscription(transcriptionRef.current);
                if (typeof onUserMessage === "function") {
                    try { onUserMessage(text); } catch (e) {
                        console.warn("[sarvam] onUserMessage callback failed:", e);
                    }
                }
                setConvTick((t) => t + 1);
                // pendingPartial is no longer needed for END_SPEECH-driven
                // commit, but we still clear it so the stopRecognition grace
                // timer doesn't double-commit.
                pendingPartial = "";
                // Brief delay then clear the interim display, giving the eye
                // a moment to see the text light up.
                setTimeout(() => setRecognisingText(""), 300);
                // Successful transcript = reset retry counter
                recognitionRetryCount.current = 0;
            }
            return;
        }

        if (type === "events" && data) {
            // VAD signals: START_SPEECH | END_SPEECH
            // With saaras:v3 these arrive BEFORE the `data` transcript, not
            // after — so we DON'T use END_SPEECH to commit anything (the
            // actual transcript comes ~400ms later as a separate `data`
            // message and commits there).
            //
            // BUT we use START_SPEECH as a critical UX signal:
            //   - Show "Listening..." in the recognizing-text area so the
            //     candidate knows they're being heard (compensates for the
            //     fact that Sarvam doesn't stream word-by-word).
            //   - Bump askForMoreTime's polling check by writing a marker
            //     into transcriptionRef? No — that would corrupt actual
            //     transcripts. Visual cue only.
            const signal = data.signal_type || data.event_type;
            if (signal === "START_SPEECH") {
                setRecognisingText("Listening…");
            } else if (signal === "END_SPEECH") {
                // Replace the "Listening…" placeholder with a "Transcribing…"
                // cue so the candidate knows we heard them and are processing.
                // The actual transcript will replace this within ~400ms.
                setRecognisingText("Transcribing…");
            }
            return;
        }

        if (type === "error" && data) {
            const errStr = data.error || "unknown Sarvam error";
            console.error("[sarvam] error from upstream:", errStr, "code:", data.code);
            setErrorMessage(`Sarvam error: ${errStr}`);
            // Fatal codes from our proxy → stop recognition; not all errors are fatal.
            if (data.code === "not_configured" || data.code === "upstream_failed") {
                recognitionActiveRef.current = false;
                setIsRecognising(false);
            }
            return;
        }

        // Unknown message type — log and ignore.
        console.warn("[sarvam] unrecognized message type:", type);
    }

    // ── Lifecycle ───────────────────────────────────────────────────────────

    async function createNewRecognizer() {
        // CONCURRENCY GUARD: if a creation is already in flight, await it
        // instead of starting a second one. Without this, two callers (e.g.
        // a useEffect re-run + a recovery retry) race: the second's
        // cleanup({force:true}) tears down the first's WS mid-handshake.
        if (_creatingPromise) {
            console.log("[sarvam] createNewRecognizer: already in flight, awaiting existing");
            return _creatingPromise;
        }
        _creatingPromise = _doCreateNewRecognizer();
        try {
            return await _creatingPromise;
        } finally {
            _creatingPromise = null;
        }
    }

    async function _doCreateNewRecognizer() {
        console.log("[sarvam] createNewRecognizer starting");

        // Track partially-acquired resources so we can release them if a
        // later step fails. Without this, a getUserMedia success + WS-open
        // failure leaks the mic stream (red indicator stays on after error).
        let acquiredStream = null;
        let openedWs = null;

        try {
            // Tear down any prior recognizer so we don't leak mics/WSes.
            await cleanup({ force: true });

            // 1) Acquire mic. Match Azure's constraints so behavior is identical
            //    for everything else in the app that uses audioStreamRef.
            const constraints = {
                video: false,
                audio: {
                    channelCount: 1,
                    sampleRate: 16000,
                    sampleSize: 16,
                    volume: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            };
            acquiredStream = await navigator.mediaDevices.getUserMedia(constraints);

            // 2) Open WebSocket to the Flask proxy.
            const wsUrl = resolveSarvamWsUrl();
            console.log("[sarvam] opening WS to", wsUrl);
            const ws = new WebSocket(wsUrl);
            openedWs = ws;

            // CRITICAL: attach onmessage BEFORE awaiting open. If we attach it
            // after the await, there's a microsecond window where a server-
            // sent message (e.g. the proxy's "not_configured" error JSON)
            // arrives before our handler is set — and gets silently dropped.
            // The handler is safe to call at any point because all refs and
            // closures it depends on are already initialized.
            ws.onmessage = (e) => {
                if (typeof e.data === "string") handleSarvamMessage(e.data);
            };

            // We need TWO behaviors for close:
            //   - Before init completes → reject the open promise so
            //     createNewRecognizer's catch path runs (proper cleanup).
            //   - After init completes  → run the persistent reconnect logic.
            // A flag lets us share one handler for both.
            let initComplete = false;
            let openPromiseReject = null;

            ws.onclose = (e) => {
                console.log("[sarvam] WS closed:", e.code, e.reason);

                if (!initComplete) {
                    // Close happened before/during init. Reject the open promise
                    // so the catch block in this function takes over.
                    if (openPromiseReject) {
                        openPromiseReject(new Error(`WS closed during init (code=${e.code})`));
                    }
                    return;
                }

                // Persistent close logic — only runs after init completed.
                _onPersistentClose(e);
            };

            ws.onerror = (e) => {
                // The onerror event in browser WebSockets carries no useful
                // detail (security feature). The follow-up onclose tells us
                // the code; we surface the user-visible error there.
                console.warn("[sarvam] WS error event (close will follow)", e);
            };

            // Wait for open or fail-fast on error/timeout/close.
            await new Promise((resolve, reject) => {
                openPromiseReject = reject;
                const timer = setTimeout(() => {
                    openPromiseReject = null;
                    reject(new Error(`WS open timeout (${WS_CONNECT_TIMEOUT_MS}ms)`));
                }, WS_CONNECT_TIMEOUT_MS);

                ws.addEventListener("open", () => {
                    clearTimeout(timer);
                    openPromiseReject = null;
                    resolve();
                }, { once: true });
                // Note: we don't attach an error/close listener here — the
                // ws.onclose handler above already handles those by calling
                // openPromiseReject when initComplete is still false.
            });
            console.log("[sarvam] WS open");

            // From here on, refs are committed and init is "complete" enough
            // that further close events should run the persistent logic.
            audioStreamRef.current = acquiredStream;
            _globalMediaStream = acquiredStream;
            recognizerRef.current = ws;
            _globalWs = ws;
            initComplete = true;

            // Define the persistent close handler. Capturing initComplete
            // happened via the closure above; this is the body of what runs
            // for "normal" close events after init.
            function _onPersistentClose(e) {
                console.log("[sarvam] WS closed:", e.code, e.reason);

                // Don't lose work: if there's an interim transcript that hadn't
                // been committed yet (END_SPEECH never arrived because the
                // connection dropped first), save it before tearing down state.
                if (pendingPartial) {
                    console.log("[sarvam] WS closed with pending partial — committing before cleanup");
                    try { commitPending(); } catch (_) {}
                }

                const wasActive = recognitionActiveRef.current;
                recognitionActiveRef.current = false;
                setIsRecognising(false);
                _globalInitialized = false;

                // Auto-recover: if the candidate was actively being recognized
                // (not a clean stopRecognition), the interview is still live,
                // and we haven't exhausted retries, attempt a reconnect with
                // exponential backoff. Matches Azure adapter behavior.
                const isCleanClose = e.code === 1000;
                if (wasActive && !isCleanClose && interviewActiveRef.current) {
                    const attempt = recognitionRetryCount.current + 1;
                    if (attempt <= RECONNECT_MAX_ATTEMPTS) {
                        recognitionRetryCount.current = attempt;
                        const delay = RECONNECT_BACKOFF_BASE_MS * attempt;
                        console.log(`[sarvam] auto-reconnect attempt ${attempt}/${RECONNECT_MAX_ATTEMPTS} in ${delay}ms`);
                        setErrorMessage(""); // clear any transient error noise
                        setTimeout(async () => {
                            if (!interviewActiveRef.current) return;
                            try {
                                await reinitializeAndStartRecognition();
                            } catch (err) {
                                console.error("[sarvam] auto-reconnect failed:", err);
                            }
                        }, delay);
                    } else {
                        console.warn("[sarvam] auto-reconnect attempts exhausted");
                        setErrorMessage("Speech connection lost. Please check your network.");
                    }
                }
            }
            // (ws.onerror is already wired up pre-open — don't re-assign it here.)

            // 4) Build the resampler (don't start yet — wait for startRecognition()).
            const resampler = await createPcmResampler({
                mediaStream: acquiredStream,
                onChunk: (jsonStr) => {
                    // Defensive: don't send after WS closed.
                    if (ws.readyState !== WebSocket.OPEN) return;
                    try {
                        ws.send(jsonStr);
                    } catch (e) {
                        // Avoid log spam — only log first send failure.
                        if (!ws.__sarvamSendErrLogged) {
                            ws.__sarvamSendErrLogged = true;
                            console.warn("[sarvam] ws.send failed:", e);
                        }
                    }
                },
                chunkDurationMs: 250,
            });
            _globalResampler = resampler;

            _globalInitialized = true;
            console.log("[sarvam] recognizer ready");
            return true;
        } catch (e) {
            console.error("[sarvam] createNewRecognizer failed:", e);
            setErrorMessage(`Sarvam initialization error: ${e?.message || e}`);
            _globalInitialized = false;

            // PARTIAL-FAILURE CLEANUP: release whatever we acquired so we don't
            // leak the mic indicator or leave a half-open WS.
            if (acquiredStream) {
                try { acquiredStream.getTracks().forEach(t => t.stop()); } catch (_) {}
                if (audioStreamRef.current === acquiredStream) audioStreamRef.current = null;
                if (_globalMediaStream === acquiredStream) _globalMediaStream = null;
            }
            if (openedWs) {
                try { openedWs.close(); } catch (_) {}
                if (recognizerRef.current === openedWs) recognizerRef.current = null;
                if (_globalWs === openedWs) _globalWs = null;
            }
            return false;
        }
    }

    async function reinitializeAndStartRecognition() {
        console.log("[sarvam] reinitializeAndStartRecognition");
        const ok = await createNewRecognizer();
        if (ok) await startRecognition();
    }

    async function startRecognition() {
        // If WS dropped between sessions, transparently reconnect.
        const ws = recognizerRef.current || _globalWs;
        if (!ws || ws.readyState !== WebSocket.OPEN) {
            console.log("[sarvam] startRecognition: no open WS, creating new recognizer");
            const ok = await createNewRecognizer();
            if (!ok) return;
        }

        if (recognitionActiveRef.current) {
            console.log("[sarvam] recognition already active");
            return;
        }

        recognitionActiveRef.current = true;
        setIsRecognising(true);
        setRecognisingText("");
        setErrorMessage("");
        pendingPartial = "";

        try {
            if (_globalResampler) await _globalResampler.start();
            console.log("[sarvam] recognition started");
        } catch (e) {
            console.error("[sarvam] resampler start failed:", e);
            recognitionActiveRef.current = false;
            setIsRecognising(false);
            setErrorMessage(`Audio start failed: ${e?.message || e}`);
        }
    }

    async function stopRecognition() {
        if (!recognitionActiveRef.current) {
            console.log("[sarvam] stopRecognition: nothing to stop");
            return;
        }
        console.log("[sarvam] stopping recognition");
        recognitionActiveRef.current = false;

        // Stop streaming audio chunks (no more onChunk callbacks).
        try {
            if (_globalResampler) await _globalResampler.stop();
        } catch (e) {
            console.warn("[sarvam] resampler stop failed (non-fatal):", e);
        }

        // Ask Sarvam to flush — usually triggers an END_SPEECH event that
        // commits the final transcript via commitPending().
        const ws = recognizerRef.current || _globalWs;
        if (ws && ws.readyState === WebSocket.OPEN) {
            try {
                ws.send(JSON.stringify({ type: "flush" }));
            } catch (e) {
                console.warn("[sarvam] flush send failed:", e);
            }
        }

        // Safety net: if Sarvam doesn't emit END_SPEECH within the grace window,
        // commit whatever's in pendingPartial so the candidate's answer isn't lost.
        setTimeout(() => {
            if (pendingPartial) {
                console.log("[sarvam] grace timer firing — committing leftover partial");
                commitPending();
            }
        }, POST_STOP_COMMIT_GRACE_MS);

        setIsRecognising(false);
        setRecognisingText("");
    }

    async function restart() {
        console.log("[sarvam] restart");
        await reinitializeAndStartRecognition();
    }

    async function cleanup({ force = false } = {}) {
        console.log("[sarvam] cleanup, force:", force);

        if (!force && _globalInitialized) {
            console.log("[sarvam] skipping cleanup — recognizer should persist");
            return;
        }

        // If a creation is in flight, let it complete (or fail) before we
        // start tearing things down. Otherwise we race with it and end up
        // closing a WS that creation just opened, leaving zombies.
        if (_creatingPromise) {
            console.log("[sarvam] cleanup: awaiting in-flight createNewRecognizer first");
            try { await _creatingPromise; } catch (_) {}
        }

        recognitionActiveRef.current = false;

        // 1) Stop resampler (no more onChunk callbacks)
        try {
            if (_globalResampler) await _globalResampler.stop();
        } catch (_) {}

        // 2) Close WS (proxy will close its Sarvam connection in turn)
        const ws = recognizerRef.current || _globalWs;
        if (ws) {
            try {
                ws.onmessage = null;
                ws.onerror = null;
                ws.onclose = null;
            } catch (_) {}
            try {
                ws.close();
            } catch (_) {}
        }

        // 3) Release mic tracks
        const stream = audioStreamRef.current || _globalMediaStream;
        if (stream) {
            try {
                stream.getTracks().forEach((t) => t.stop());
            } catch (_) {}
        }

        // 4) Null everything
        recognizerRef.current = null;
        audioStreamRef.current = null;
        _globalWs = null;
        _globalResampler = null;
        _globalMediaStream = null;
        _globalInitialized = false;
        pendingPartial = "";
    }

    function reuseExisting() {
        if (_globalInitialized && _globalWs && _globalWs.readyState === WebSocket.OPEN) {
            console.log("[sarvam] reusing existing recognizer from previous mount");
            recognizerRef.current = _globalWs;
            audioStreamRef.current = _globalMediaStream;
            // Re-bind the message handler so it closes over the current refs.
            _globalWs.onmessage = (e) => {
                if (typeof e.data === "string") handleSarvamMessage(e.data);
            };
            return true;
        }
        return false;
    }

    function isInitialized() {
        return _globalInitialized;
    }

    return {
        providerName: "sarvam",
        createNewRecognizer,
        reinitializeAndStartRecognition,
        startRecognition,
        stopRecognition,
        restart,
        cleanup,
        reuseExisting,
        isInitialized,
    };
}
